import { Directive, ElementRef, Input, NgZone, OnDestroy, OnInit, Renderer2 } from '@angular/core';

// Draws a custom, always-visible scrollbar over the host element instead of relying on the
// native one - OS-level "auto-hide scrollbars" settings (macOS/trackpad Chrome & Safari) make it
// impossible to guarantee a persistently visible native scrollbar via CSS alone.
@Directive({
	selector: '[appPermanentScrollbar]',
	standalone: false
})
export class PermanentScrollbarDirective implements OnInit, OnDestroy {
	private static readonly MIN_THUMB_HEIGHT = 24;
	private static readonly SCROLLBAR_WIDTH = 8;

	@Input() appPermanentScrollbar: boolean | string = true;
	@Input() appPermanentScrollbarReserveSpace: boolean | string = true;

	private track: HTMLElement;
	private thumb: HTMLElement;
	private resizeObserver?: ResizeObserver;
	private mutationObserver?: MutationObserver;
	private isDragging = false;
	private dragStartY = 0;
	private dragStartScrollTop = 0;

	constructor(
		private el: ElementRef<HTMLElement>,
		private renderer: Renderer2,
		private zone: NgZone
	) {}

	private get isEnabled(): boolean {
		return this.appPermanentScrollbar !== false && this.appPermanentScrollbar !== 'false';
	}

	ngOnInit(): void {
		if (!this.isEnabled) return;

		this.zone.runOutsideAngular(() => {
			this.buildScrollbar();
			this.attachListeners();
			this.update();
		});
	}

	ngOnDestroy(): void {
		if (!this.track) return;

		const host = this.el.nativeElement;
		host.removeEventListener('scroll', this.onScroll);
		window.removeEventListener('resize', this.onScroll);
		window.removeEventListener('mousemove', this.onDragMove);
		window.removeEventListener('mouseup', this.onDragEnd);
		window.removeEventListener('touchmove', this.onDragMove);
		window.removeEventListener('touchend', this.onDragEnd);
		this.resizeObserver?.disconnect();
		this.mutationObserver?.disconnect();
		this.track?.remove();
	}

	private buildScrollbar(): void {
		const host = this.el.nativeElement;
		this.renderer.addClass(host, 'has-permanent-scrollbar');

		if (this.appPermanentScrollbarReserveSpace !== false && this.appPermanentScrollbarReserveSpace !== 'false') {
			const existingPaddingRight = parseFloat(getComputedStyle(host).paddingRight) || 0;
			this.renderer.setStyle(host, 'padding-right', `${existingPaddingRight + PermanentScrollbarDirective.SCROLLBAR_WIDTH}px`);
		}

		this.track = this.renderer.createElement('div');
		this.renderer.addClass(this.track, 'permanent-scrollbar-track');

		this.thumb = this.renderer.createElement('div');
		this.renderer.addClass(this.thumb, 'permanent-scrollbar-thumb');

		this.renderer.appendChild(this.track, this.thumb);
		this.renderer.appendChild(document.body, this.track);
	}

	private attachListeners(): void {
		const host = this.el.nativeElement;
		host.addEventListener('scroll', this.onScroll, { passive: true });
		window.addEventListener('resize', this.onScroll, { passive: true });

		this.thumb.addEventListener('mousedown', this.onDragStart);
		this.thumb.addEventListener('touchstart', this.onDragStart, { passive: true });

		this.resizeObserver = new ResizeObserver(() => this.update());
		this.resizeObserver.observe(host);

		this.mutationObserver = new MutationObserver(() => this.update());
		this.mutationObserver.observe(host, { childList: true, subtree: true });
	}

	private onScroll = (): void => this.update();

	private onDragStart = (e: MouseEvent | TouchEvent): void => {
		e.preventDefault();
		this.isDragging = true;
		this.dragStartY = this.eventY(e);
		this.dragStartScrollTop = this.el.nativeElement.scrollTop;
		window.addEventListener('mousemove', this.onDragMove);
		window.addEventListener('mouseup', this.onDragEnd);
		window.addEventListener('touchmove', this.onDragMove, { passive: false });
		window.addEventListener('touchend', this.onDragEnd);
	};

	private onDragMove = (e: MouseEvent | TouchEvent): void => {
		if (!this.isDragging) return;
		e.preventDefault();

		const host = this.el.nativeElement;
		const { scrollHeight, clientHeight } = host;
		const trackHeight = host.getBoundingClientRect().height;
		const thumbHeight = this.thumb.getBoundingClientRect().height;
		const scrollableTrack = trackHeight - thumbHeight;
		const scrollableContent = scrollHeight - clientHeight;
		if (scrollableTrack <= 0 || scrollableContent <= 0) return;

		const deltaY = this.eventY(e) - this.dragStartY;
		host.scrollTop = this.dragStartScrollTop + (deltaY * scrollableContent) / scrollableTrack;
	};

	private onDragEnd = (): void => {
		this.isDragging = false;
		window.removeEventListener('mousemove', this.onDragMove);
		window.removeEventListener('mouseup', this.onDragEnd);
		window.removeEventListener('touchmove', this.onDragMove);
		window.removeEventListener('touchend', this.onDragEnd);
	};

	private eventY(e: MouseEvent | TouchEvent): number {
		return e instanceof MouseEvent ? e.clientY : e.touches[0].clientY;
	}

	private update(): void {
		const host = this.el.nativeElement;
		const { scrollTop, scrollHeight, clientHeight } = host;
		const hostRect = host.getBoundingClientRect();

		if (scrollHeight <= clientHeight) {
			this.renderer.setStyle(this.track, 'display', 'none');
			return;
		}

		const thumbHeight = Math.max(
			(clientHeight / scrollHeight) * hostRect.height,
			PermanentScrollbarDirective.MIN_THUMB_HEIGHT
		);
		const scrollableTrack = hostRect.height - thumbHeight;
		const scrollableContent = scrollHeight - clientHeight;
		const thumbTop = scrollableContent > 0 ? (scrollTop / scrollableContent) * scrollableTrack : 0;

		this.renderer.setStyle(this.track, 'display', 'block');
		this.renderer.setStyle(this.track, 'top', `${hostRect.top}px`);
		this.renderer.setStyle(this.track, 'left', `${hostRect.right - PermanentScrollbarDirective.SCROLLBAR_WIDTH}px`);
		this.renderer.setStyle(this.track, 'height', `${hostRect.height}px`);
		this.renderer.setStyle(this.track, 'width', `${PermanentScrollbarDirective.SCROLLBAR_WIDTH}px`);

		this.renderer.setStyle(this.thumb, 'height', `${thumbHeight}px`);
		this.renderer.setStyle(this.thumb, 'transform', `translateY(${thumbTop}px)`);
	}
}
